<script setup lang="ts">
import { useAuthStore } from '#modules/auth/store/auth';
import { storeToRefs } from 'pinia';
import HeroSearch from '~/components/HeroSearch.vue';
import HowItWorks from '~/components/HowItWorks.vue';
import JobsGrid from '~/components/JobsGrid.vue';

definePageMeta({
  layout: 'default',
});

const { hasLoggedInUser, hasCompletedOnboarding } = storeToRefs(useAuthStore());
const config = useRuntimeConfig();

// Enhanced SEO with useSeoMeta (recommended by Nuxt SEO)
useSeoMeta({
  title: 'Earthworks Connect',
  description:
    'Earthworks Connect is the online platform to find trusted earthmoving and excavation contractors. Post jobs, get offers, and hire professionals for excavation, land clearing, site prep, and bulk earthworks quickly and easily.',
  // Open Graph / Facebook
  ogType: 'website',
  ogSiteName: 'Earthworks Connect',
  ogTitle: 'Earthworks Connect',
  ogDescription:
    'Earthworks Connect is the online platform to find trusted earthmoving and excavation contractors. Post jobs, get offers, and hire professionals for excavation, land clearing, site prep, and bulk earthworks quickly and easily.',
  ogUrl: config.public.baseURL,
  ogImage: `${config.public.baseURL}/earth.jpg`,
  // Twitter
  card: 'summary_large_image',
  twitterTitle: 'Earthworks Connect',
  twitterDescription:
    'Earthworks Connect is the online platform to find trusted earthmoving and excavation contractors. Post jobs, get offers, and hire professionals for excavation, land clearing, site prep, and bulk earthworks quickly and easily.',
  twitterImage: `${config.public.baseURL}/earth.jpg`,
});

// Structured data for Home/Construction business
useSchemaOrg([
  defineWebSite({
    name: 'Earthworks Connect',
    url: config.public.baseURL,
    description:
      'Connect People and Contractors - Earthworks Connect is the online platform to find trusted earthmoving and excavation contractors. Post jobs, get offers, and hire professionals for excavation, land clearing, site prep, and bulk earthworks quickly and easily.',
    inLanguage: 'en-AU',
  }),
  defineOrganization({
    name: 'Earthworks Connect',
    url: config.public.baseURL,
    logo: `${config.public.baseURL}/earth.jpg`,
    sameAs: [], // Add social media URLs if available
  }),
  defineWebPage({
    name: 'Earthworks Connect',
    description:
      'Earthworks Connect is the online platform to find trusted earthmoving and excavation contractors. Post jobs, get offers, and hire professionals for excavation, land clearing, site prep, and bulk earthworks quickly and easily.',
  }),
]);

// Set canonical URL
useHead({
  link: [
    {
      rel: 'canonical',
      href: config.public.baseURL,
    },
  ],
});

const features = [
  {
    title: 'Modern Design System',
    description: 'Built with Earthworks Connect brand colors and typography',
  },
  {
    title: 'Responsive Navigation',
    description: 'Seamless experience across desktop, tablet, and mobile devices',
  },
  {
    title: 'Vuetify 3',
    description: 'Powered by the latest Vuetify framework with Material Design',
  },
  {
    title: 'Type-Safe',
    description: 'Full TypeScript support with Vue 3 Composition API',
  },
];

onBeforeMount(async () => {
  if (!hasCompletedOnboarding.value) {
    await navigateTo('/onboarding/goal-selection');
  }
});
</script>

<template>
  <v-container class="landing-page page-max-1200 py-md-14 py-sm-12">
    <HeroSearch class="mb-3" />
    <JobsGrid :per-page="hasLoggedInUser ? 9 : 6" :show-pagination="hasLoggedInUser" class="mt-14" />
    <HowItWorks v-if="!hasLoggedInUser" />
  </v-container>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/colors' as *;
@use '#core/assets/scss/partials/fonts' as *;

.landing-page {
  min-height: calc(100vh - 120px);
  max-width: 1200px;

  @media (max-width: 600px) {
    padding-top: 35px !important;
  }
}

.display-title {
  font-family: $font-family-display;
  font-size: clamp(32px, 5vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  color: $text-color-primary;
}

.body-text {
  font-family: $font-family-primary;
  font-size: 18px;
  line-height: 1.6;
  color: $text-color-secondary;
}

.feature-card {
  border: 1px solid $border-default;
  border-radius: 16px;
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
}

.card-title {
  font-family: $font-family-display;
  font-size: 20px;
  font-weight: 700;
  color: $text-color-primary;
}

.card-description {
  font-family: $font-family-primary;
  font-size: 14px;
  line-height: 1.5;
  color: $text-color-secondary;
}

@media (max-width: 768px) {
  .landing-page {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
}
</style>
